import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { canEditCertGate } from "@/features/certgate/permissions";
import { isActiveShiftLead, leadAtBranch } from "@/lib/auth/shift-lead";

/** Phòng đứng quầy — xem và đổi gán suất là việc hằng ngày của họ. */
const DISPATCH_DEPTS = new Set(["reception"]);

function inDispatchDept(staff: Staff): boolean {
  return DISPATCH_DEPTS.has((staff.department || "").trim().toLowerCase());
}

/**
 * Ai được mở BẢNG suất của cơ sở. Bản sao đúng cổng backend
 * `orShiftLead(auth.AllowOrDept("dispatch.read", "reception"))` (handler.go).
 *
 * Bạn chụp và bạn trang điểm cố tình KHÔNG nằm ở đây — họ không cần nhìn cả
 * bảng, chỉ cần màn "Suất của tôi" trên điện thoại (`/dispatch/my` là
 * auth-only, không đi qua hàm này).
 */
export function canSeeDispatchBoard(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  if (hasPermission(ACTION.DispatchRead)) return true;
  if (inDispatchDept(staff)) return true;
  // Ca trưởng đang trong giờ ca ± 2 tiếng (CEO 27/09/2026) — bản sao cổng
  // `orShiftLead` của máy chủ. Cơ sở cụ thể do máy chủ soát.
  return isActiveShiftLead();
}

/**
 * Xem bảng CHỈ nhờ đang làm ca trưởng (vai và phòng không cho xem): màn khoá
 * ô chọn cơ sở về đúng cơ sở của ca.
 */
export function seesBoardOnlyAsShiftLead(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  return !hasPermission(ACTION.DispatchRead) && !inDispatchDept(staff) && isActiveShiftLead();
}

/**
 * Phòng NHẬN suất chụp Profile. Có cả mã nhóm chung `photo` vì leader khối ảnh
 * mang đúng mã đó làm department (khuôn `PHOTO_DEPTS` của ekip-shift), còn ba
 * mã cụ thể là của thợ.
 */
const SLOT_DEPTS = new Set([
  "photo",
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
  "makeup",
]);

/**
 * Vai KHÔNG bao giờ được máy xếp vào suất dù mang mã phòng ảnh/makeup: QC soi
 * chất lượng đội mình, quản lý cụm giám sát, `viewer` chỉ đọc.
 *
 * ⚠️ Chặn `qc` ở đây là việc THẬT, không phải phòng xa: `PAGE_ACCESS.qc_photo`
 * và `qc_makeup` được SINH từ `manager_photo`/`manager_makeup`
 * (`qcPagesOf`, lib/auth/page-access.ts), nên QC khối ảnh tự động thừa kế
 * trang "Suất của tôi" mà không ai gõ tay. Cổng nội dung này là chỗ duy nhất
 * cắt lại.
 */
const NO_SLOT_ROLES = new Set([
  "qc",
  "area_manager",
  "viewer",
  // Vai `shift_lead` (quyết định 0209) ĐÃ KHOÁ 27/09/2026 — giữ ở đây cho hồ sơ
  // cũ còn mang mã. Người làm ca trưởng THEO CA (màn Ca vận hành) giữ nguyên
  // vai của mình và VẪN nhận suất.
  "shift_lead",
]);

/**
 * Ai thấy màn "Suất của tôi" (/my-dispatch) + thẻ tóm tắt trên Tổng quan.
 *
 * KHÔNG có bản sao ở backend để soi: `/dispatch/my` là auth-only, ai gọi cũng
 * được và trả về đúng suất của chính người gọi. Vì thế đây thuần tuý là luật
 * ẩn/hiện của giao diện — người ngoài khối photo/makeup mở URL thẳng vẫn thấy
 * một danh sách rỗng, không phải lỗi 403.
 */
export function canSeeMyDispatch(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  if (NO_SLOT_ROLES.has((staff.role || "").trim().toLowerCase())) return false;
  return SLOT_DEPTS.has((staff.department || "").trim().toLowerCase());
}

/**
 * Mục "Vì sao tôi chưa được suất?" trên màn Suất của tôi (QĐ 0142 §7.2).
 * Bản sao cổng backend `auth.Allow("dispatch.why.read")` — Allow thuần, không
 * lối phòng ban, không cần hồ sơ: máy chủ tự chỉ trả dòng của chính người gọi.
 * Thiếu quyền thì màn ẨN cả mục chứ không vẽ hộp lỗi 403.
 */
export function canReadDispatchWhy(): boolean {
  return hasPermission(ACTION.DispatchWhyRead);
}

/**
 * Ai thấy nút gán tay / đổi gán. Bản sao cổng
 * `orShiftLead(auth.AllowOrDept("dispatch.override", "reception"))` — tức có
 * quyền, hoặc lễ tân, hoặc đang là ca trưởng. Từng suất còn phải qua
 * `overrideLockReason` (lễ tân chỉ gán suất trống, không đổi suất của mình).
 *
 * ⚠️ KHÔNG gộp với `canSeeDispatchBoard`: hai action có danh sách vai KHÁC
 * nhau — QC giữ `dispatch.read` nhưng cố ý KHÔNG có `dispatch.override`
 * (quyết định 0036: đổi gán là quyền VẬN HÀNH, QC chỉ soi chất lượng). Gộp
 * lại là lặng lẽ phát quyền đổi gán cho QC.
 */
export function canOverrideDispatch(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  if (hasPermission(ACTION.DispatchOverride)) return true;
  if (inDispatchDept(staff)) return true;
  return isActiveShiftLead();
}

/**
 * Lý do KHOÁ nút gán/đổi gán trên MỘT suất, hoặc null khi bấm được. Bản sao
 * `overrideRight` của máy chủ (orderdispatch/shiftlead_power.go, CEO chốt
 * 27/09/2026 — 1a·2a): nút mà người xem không bấm được thì khoá sẵn và nói lý
 * do ngay trên nút, đừng để bấm rồi mới ăn 403.
 *
 *   · suất chính mình đang giữ → không ai đổi hộ qua cửa này;
 *   · có `dispatch.override` (trưởng cửa hàng, trưởng phòng, ban giám đốc) → bấm được;
 *   · đang là ca trưởng tại cơ sở của suất → bấm được;
 *   · lễ tân không phải ca trưởng → chỉ gán suất CÒN TRỐNG.
 */
export function overrideLockReason(
  slot: { staffId: string; branch: string },
  staff: Staff | null | undefined,
): string | null {
  if (!staff) return "Bạn chưa đăng nhập";
  if (slot.staffId && slot.staffId === staff.id) {
    return "Suất của bạn — nhờ trưởng cửa hàng đổi, hoặc dùng Trả suất";
  }
  if (hasPermission(ACTION.DispatchOverride)) return null;
  if (leadAtBranch(slot.branch)) return null;
  if (inDispatchDept(staff)) {
    return slot.staffId ? "Chỉ ca trưởng hoặc trưởng cửa hàng đổi người đang giữ suất" : null;
  }
  return "Chỉ ca trưởng của cơ sở này đổi được";
}
/**
 * Ai mở được màn CÀI ĐẶT máy chia đơn (/dispatch-settings).
 *
 * ⚠️ Trang này có BỐN thẻ và BỐN cổng khác nhau, cố ý — đừng gộp:
 *   · Cấu hình máy       → `dispatch.config.write` (quản trị, giám đốc)
 *   · Người được chỉ định → `dispatch.designee.write` (+ trưởng cửa hàng)
 *   · Danh mục nhãn      → `ordertag.manage` (quản trị, giám đốc)
 *   · Chứng chỉ làm việc → `certgate.config.write` (quản trị, giám đốc — QĐ 0101)
 *
 * Trưởng cửa hàng chỉ có cái GIỮA: người được chỉ định cho nhãn khó là quyết
 * định của CƠ SỞ, còn luật máy và danh mục nhãn là của cả hệ. Vào trang bằng
 * "có ít nhất một quyền", rồi từng thẻ tự gác lấy — gộp một cổng chung là
 * trưởng cửa hàng bấm sang thẻ Cấu hình rồi ăn 403 giữa ca.
 *
 * Cả bốn hàm KHÔNG nhận `staff`: cửa backend gác bằng `auth.Allow` thuần,
 * không có lối phòng ban như bảng suất, nên ở đây cũng không được có.
 */
export function canSeeDispatchSettings(): boolean {
  return (
    canEditDispatchConfig() || canEditDesignees() || canManageOrderTags() || canEditCertGate()
  );
}

/** Thẻ "Cấu hình máy" — bật/tắt, giờ chạy, hạn nhận. GET lẫn PUT cùng cổng. */
export function canEditDispatchConfig(): boolean {
  return hasPermission(ACTION.DispatchConfigWrite);
}

/** Thẻ "Người được chỉ định" — khai theo TỪNG cơ sở. */
export function canEditDesignees(): boolean {
  return hasPermission(ACTION.DispatchDesigneeWrite);
}

/** Thẻ "Danh mục nhãn" — thêm/sửa/tắt nhãn của cả hệ. */
export function canManageOrderTags(): boolean {
  return hasPermission(ACTION.OrderTagManage);
}

/*
 * Bốn hàm dưới đây gác màn /dispatch-settings; hai hàm cuối file gác thẻ "Cài
 * đặt" nằm TRONG Bảng suất. Hai bộ tồn tại song song có chủ đích: hai màn khác
 * nhau, hai đường vào khác nhau, nhưng CÙNG sửa một khoá cấu hình. Vì cửa PUT
 * thay trọn gói nên màn nào cũng phải nạp trọn bản hiện có rồi gửi trọn về —
 * xem chú thích ở `DispatchConfigSchema`.
 */

/**
 * Ai thấy tab "Cài đặt" trong Bảng suất cơ sở (bật/tắt máy theo cơ sở + 11
 * tham số thuật toán + nút rà tay).
 *
 * ⚠️ CỐ Ý không có nhánh `inDispatchDept`, khác hẳn hai hàm trên: backend gác
 * `/dispatch/config` và `/dispatch/sweep` bằng `auth.Allow` THUẦN, không phải
 * `AllowOrDept(..., "reception")` (handler.go). Thêm nhánh phòng ban ở đây là
 * FE mở tab cho lễ tân rồi họ bấm Lưu ăn 403 — một cái cửa mở ra chỉ để đóng
 * sập vào mặt người dùng.
 *
 * Hai action tách riêng nhưng cùng tập vai {admin, director}, và cả hai đều
 * chỉ sống trong tab này: gộp làm một cổng hiển thị. Nút "Rà lại ngay" bên
 * trong tab vẫn hỏi RIÊNG `DispatchSweep` — nếu mai này hai tập vai tách ra
 * thì chỗ đó đã đúng sẵn.
 */
export function canConfigureDispatch(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.DispatchConfigWrite);
}

/** Ai được bấm "Rà lại ngay" — gán người thật, bắn thông báo thật cho thợ. */
export function canSweepDispatch(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.DispatchSweep);
}

/**
 * Ai mở được màn "Điểm hành vi" (/dispatch-points).
 *
 * KHÔNG nhận `staff` và CỐ Ý không có nhánh phòng ban: cửa máy chủ
 * `GET /dispatch/points` gác bằng `auth.Allow("dispatch.points.read")` THUẦN
 * (handler.go), không phải `AllowOrDept(..., "reception")` như bảng suất. Mở
 * thêm cho lễ tân ở đây là bày ra một mục menu chỉ để ăn 403 khi bấm.
 */
export function canSeeDispatchPoints(): boolean {
  return hasPermission(ACTION.DispatchPointsRead);
}

/**
 * Ai bấm được nút "Gỡ phạt".
 *
 * ⚠️ HẸP HƠN `canSeeDispatchPoints` đúng MỘT vai — đừng gộp: máy chủ cấp
 * `dispatch.points.read` cho cả người giám sát nhiều cơ sở, nhưng
 * `dispatch.points.void` thì KHÔNG (actions.go). Gộp một cổng là nút hiện ra
 * cho người bấm vào sẽ ăn 403.
 */
export function canVoidDispatchPoint(): boolean {
  return hasPermission(ACTION.DispatchPointsVoid);
}
